import { DownloadOutlined } from '@ant-design/icons';
import { Modal, Space, Typography, Upload, UploadFile } from 'antd';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { ConversationContentProps } from '.';
import { imageFileExtensions } from '../../../../constants/imageFileExtensions';
import useEventListener from '../../../../hooks/useEventListener';
import useToggle from '../../../../hooks/useToggle';
import { Attachment } from '../../../../model/types';
import { parseUserIdFromSerializedValue } from '../../../../utils/forms/formHelper.parser';
import CommentComponent from '../../../Comment';

const isImageFile = (mimeType: string): boolean => {
    return imageFileExtensions.includes(mimeType);
};

export default function DefaultContent({ conversation, showAttachments = true, to }: ConversationContentProps) {
    const { body, commentCCs = [], id, publicVisible, toEmail: toEmailOnConversation, attachments } = conversation;
    const toEmail = to ?? toEmailOnConversation;
    const defaultFileList = attachments
        ?.filter((attachment) => !attachment.inline)
        ?.map((attachment) => ({
            ...attachment,
            name: attachment.filename,
            thumbUrl: isImageFile(attachment?.mimeType)
                ? attachment.objectThumbUrl
                : process.env.NEXT_PUBLIC_GRISPI_WP_URL,
            uid: attachment.id,
            url: attachment.objectUrl,
            status: 'done'
        }));

    const [isPreviewOpen, , open, close] = useToggle();
    const [previewFile, setPreviewFile] = useState<Attachment>(null);
    const { push } = useRouter();

    const handlePreview = (file: Attachment | UploadFile) => {
        if ('objectThumbUrl' in file) {
            if (isImageFile(file.mimeType)) {
                setPreviewFile(file);
                open();
                return;
            }
            window.open(file?.objectThumbUrl);
        }
    };

    useEventListener('mention-clicked', async (e: WindowEventMap['mention-clicked']) => {
        const userId = parseUserIdFromSerializedValue(e.value?.id);
        await push(`/users/${userId}/tickets/assigned`);
    });

    return (
        <Space direction="vertical" size={0} className="full-width">
            {toEmail && (
                <Typography.Paragraph type="secondary" style={{ fontSize: '0.75rem' }} className="full-width">
                    <strong style={{ display: 'inline', color: 'black' }}>{`To: `}</strong>
                    {toEmail}
                </Typography.Paragraph>
            )}

            {publicVisible && commentCCs.length > 0 && (
                <Typography.Paragraph
                    type="secondary"
                    style={{ fontSize: '0.75rem', marginTop: '-1em' }}
                    className="full-width"
                >
                    <strong style={{ display: 'inline', color: 'black' }}>{`CC: `}</strong>
                    {commentCCs.map((cc, index, ccArray) => (
                        <span key={cc}>{`${cc}${index === ccArray.length - 1 ? ' ' : ', '}`}</span>
                    ))}
                </Typography.Paragraph>
            )}
            <CommentComponent
                content={body}
                type={publicVisible ? 'public' : 'internal'}
                key={id}
                id={id}
                showTranslate
            />
            {showAttachments && (
                <Upload
                    className="conversation-attachment-list"
                    listType="picture"
                    // className={clsx(!publicVisible && 'internal-note')}
                    showUploadList={{
                        downloadIcon: <DownloadOutlined />,
                        showDownloadIcon: true,
                        showRemoveIcon: false
                    }}
                    onPreview={handlePreview}
                    fileList={defaultFileList as unknown as UploadFile[]}
                />
            )}

            <Modal
                className="comment-attachment-preview"
                open={isPreviewOpen}
                title={previewFile?.filename}
                footer={null}
                onCancel={close}
                destroyOnClose
            >
                <img alt={previewFile?.filename} src={previewFile?.objectThumbUrl} style={{ maxWidth: '100%' }} />
            </Modal>
        </Space>
    );
}
